import { useEffect, useRef, useCallback } from 'react';
import { logger } from '../utils/logger';

interface ActivityTrackerOptions {
  onActivity?: () => void;
  throttleMs?: number; // Throttle activity events (default: 1000ms)
  events?: string[]; // Events to track
}

/**
 * Hook to track user activity with proper throttling
 * Prevents excessive event firing while ensuring accurate activity detection
 */
export const useActivityTracker = ({
  onActivity,
  throttleMs = 1000,
  events = ['mousedown', 'keydown', 'touchstart', 'scroll', 'mousemove', 'input', 'change']
}: ActivityTrackerOptions = {}) => {
  const lastActivityRef = useRef<number>(Date.now());
  const throttleTimerRef = useRef<NodeJS.Timeout | null>(null);

  const handleActivity = useCallback(() => {
    const now = Date.now();
    
    // Throttle: Only trigger if enough time has passed
    if (now - lastActivityRef.current < throttleMs) {
      return;
    }

    lastActivityRef.current = now;
    
    // Update localStorage
    localStorage.setItem('last_activity', now.toString());
    
    logger.activity('User activity detected', {
      timestamp: new Date(now).toISOString()
    });

    if (onActivity) {
      onActivity();
    }
  }, [onActivity, throttleMs]);

  useEffect(() => {
    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Initial activity
    handleActivity();

    return () => {
      // Cleanup
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      
      if (throttleTimerRef.current) {
        clearTimeout(throttleTimerRef.current);
      }
    };
  }, [events, handleActivity]);

  return {
    lastActivity: lastActivityRef.current,
    getTimeSinceLastActivity: () => Date.now() - lastActivityRef.current
  };
};
